import { useEffect, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Navbar } from './Navbar';
import { Footer } from './Footer';
import { useEmailSync } from '../../hooks/useEmailSync';
import { useTodoCountsOrZero } from '../../hooks/useTodos';
import { contactsAPI } from '../../api/endpoints';
import { prewarmEmailImages } from '../../utils/emailImages';

interface LayoutProps {
  children: ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const { isSyncing, lastSyncAt, triggerSync } = useEmailSync();

  // Load the email image library up front. Every stored mail body references its images by
  // `cid:`, and resolving one has to be synchronous because it happens inside the contact
  // overlay's render (docs/EMAIL_IMAGES.md) — so the bytes must already be here. Idempotent.
  useEffect(() => {
    void prewarmEmailImages();
  }, []);

  // Counts come from one unfiltered aggregation, shared with the todo page's header and
  // filter dropdowns, so the badge and the page always agree.
  const { overdue: overdueTodoCount, today: dueTodoCount } = useTodoCountsOrZero();

  // Unread received emails — drives the inbox badge
  const { data: unreadData } = useQuery({
    queryKey: ['inbox', 'unread-count'],
    queryFn: () => contactsAPI.inbox({ direction: 'received', is_read: false, page_size: 1 }).then((r) => r.data),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const unreadEmailCount = unreadData?.total ?? 0;

  // Scheduled (pending) emails — drives the second inbox badge
  const { data: scheduledData } = useQuery({
    queryKey: ['inbox', 'scheduled-count'],
    queryFn: () => contactsAPI.inbox({ direction: 'scheduled', page_size: 1 }).then((r) => r.data),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const scheduledEmailCount = scheduledData?.total ?? 0;

  return (
    <div className="min-h-screen flex flex-col relative">
      <Navbar isSyncing={isSyncing} lastSyncAt={lastSyncAt} onSync={triggerSync} unreadEmailCount={unreadEmailCount} scheduledEmailCount={scheduledEmailCount} overdueTodoCount={overdueTodoCount} dueTodoCount={dueTodoCount} />
      <main className="flex-1 relative z-10">{children}</main>
      <Footer />
    </div>
  );
}
